import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { useCurrentUser } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { useStepOrchestratorContext } from '@savgroup-front-common/core/molecules/StepsOrchestrator';

import useGetClientInformation from '@/view/app/hooks/useGetClientInformation';
import { useSetClaimGroupIssueCommand } from '@/view/app/hooks/useSetClaimGroupIssueCommand';

import { useGetIssues } from './hooks/useGetIssues';
import IssuesPageSchema from './IssuesPage.schema';
import { ChoiceIssueValues, IssuesReasonsPageForm } from './IssuesPage.types';

const useIssuesPage = () => {
  const { id: ownerId } = useCurrentUser();
  const { clientInformation } = useGetClientInformation();
  const { nextStep, values } =
    useStepOrchestratorContext<IssuesReasonsPageForm>();

  const { issuesSummaries } = useGetIssues({
    ownerId,
    sellerId: clientInformation?.sellerId,
  });

  const { handleSetClaimGroupIssueCommand } = useSetClaimGroupIssueCommand();

  const formContext = useForm<ChoiceIssueValues>({
    resolver: yupResolver(IssuesPageSchema),
    mode: REVALIDATE_MODES.ON_CHANGE,
    defaultValues: {
      choiceIssue: values.selectedIssue?.id ?? values.order?.issueId,
    },
  });

  const onSubmit = async ({ choiceIssue }: { choiceIssue: string }) => {
    const selectedIssue = issuesSummaries?.find(
      (issue) => issue.id === choiceIssue,
    );

    const claimGroupId = (values.order?.products ?? [])
      .map((product) => product.claimGroupId)
      .find((id): id is string => Boolean(id));

    if (claimGroupId && selectedIssue?.id) {
      const response = await handleSetClaimGroupIssueCommand({
        claimGroupId,
        issueId: selectedIssue.id,
      });

      if (!response) {
        return;
      }
    }

    nextStep({ selectedIssue });
  };

  return {
    issues: issuesSummaries || [],
    issuesCount: issuesSummaries?.length || 0,
    formContext,
    onSubmit,
  };
};

export default useIssuesPage;
